import React, { useEffect, useState, useCallback } from 'react';
import { CheckCircle, Clock, LogIn, LogOut, Plus, Edit2, Trash2, X, AlertCircle, Search, Calendar, User, BarChart3, TrendingUp, FileText, Camera, MapPin, Navigation, Eye, Printer, Download } from 'lucide-react';
import { getAbsensi, createAbsensi, updateAbsensi, deleteAbsensi, absensiCheckin, absensiCheckout, getAbsensiStats, getAbsensiRekap, getAbsensiRekapOrang, getKaryawan, getBagian } from '../api';
import { useAuth } from '../AuthContext';
import CameraCapture from '../components/CameraCapture';
import LocationCapture from '../components/LocationCapture';

const STATUS_OPTIONS = ['Hadir', 'Izin', 'Sakit', 'Cuti', 'Alpha'];

const emptyForm = { karyawan_id: '', tanggal: '', check_in: '', check_out: '', status: 'Hadir', keterangan: '' };
const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

// ======== Stat Card Mini ========
function StatCard({ icon: Icon, label, value, color, sublabel }) {
  return (
    <div className="stat-card">
      <div className="stat-card-body">
        <div className="stat-card-content">
          <h5 className="stat-card-label">{label}</h5>
          <span className="stat-card-value" style={{ color }}>{value ?? 0}</span>
          {sublabel && <span className="stat-card-sublabel">{sublabel}</span>}
        </div>
        <div className="stat-card-icon-col">
          <div className="stat-icon-shape" style={{ background: (color || '#11cdef') + '18', color: color || '#11cdef' }}>
            <Icon size={22} />
          </div>
        </div>
      </div>
    </div>
  );
}

// ======== Status Badge ========
function StatusBadge({ status }) {
  const s = (status || '');
  let bg = '#e9ecef', color = '#525f7f';
  if (s === 'Hadir') { bg = '#d1fae5'; color = '#065f46'; }
  if (s === 'Izin') { bg = '#dbeafe'; color = '#1e40af'; }
  if (s === 'Sakit') { bg = '#fef3c7'; color = '#92400e'; }
  if (s === 'Cuti') { bg = '#f3e8ff'; color = '#6d28d9'; }
  if (s === 'Alpha') { bg = '#fee2e2'; color = '#991b1b'; }
  return <span className="badge" style={{ background: bg, color }}>{s}</span>;
}

// ======== Area Badge (Dalam/Luar Area radar) ========
function AreaBadge({ area }) {
  if (!area) return <span className="text-muted">-</span>;
  const inside = area === 'INSIDE';
  return (
    <span className="badge" style={{
      background: inside ? '#d1fae5' : '#fee2e2',
      color: inside ? '#065f46' : '#991b1b',
      whiteSpace: 'nowrap',
    }}>
      {inside ? '📍 Dalam Area' : '📍 Luar Area'}
    </span>
  );
}

// ======== Format helpers ========
// Format jam kerja (desimal jam) → "8j 30m"
const fmtJamKerja = (jk) => {
  if (jk == null) return '-';
  const totalMenit = Math.round(jk * 60);
  const j = Math.floor(totalMenit / 60);
  const m = totalMenit % 60;
  if (j === 0) return `${m}m`;
  return m > 0 ? `${j}j ${m}m` : `${j}j`;
};

// Format jarak (meter) → "1.250 m" atau "1,2 km"
const fmtJarak = (m) => {
  if (m == null) return '-';
  if (m >= 1000) return `${(m / 1000).toFixed(1).replace('.', ',')} km`;
  return `${Math.round(m).toLocaleString('id-ID')} m`;
};

// ======== Roster Harian (grid karyawan x tanggal) ========
// Menampilkan rekap bulanan ala referensi sdm.bumiputera.net:
// baris = karyawan, kolom = tanggal 1..31, sel = simbol status + jam masuk.
function RekapRoster({ rekapData, bulan, onOpenDetail }) {
  const karyawan = rekapData?.karyawan || [];
  if (!bulan) return null;

  // Jumlah hari dalam bulan
  const [th, bln] = bulan.split('-').map(Number);
  const totalDays = new Date(th, bln, 0).getDate();
  const days = Array.from({ length: totalDays }, (_, i) => i + 1);

  // Simbol status (ala referensi: ✓ hadir, T telat, A alpha, dst)
  const symOf = (status) => {
    const map = { Hadir: '✓', Alpha: 'A', Izin: 'I', Sakit: 'S', Cuti: 'C' };
    return map[status] || status?.charAt(0) || '−';
  };
  const colorOf = (status) => {
    const map = { Hadir: '#16a34a', Alpha: '#dc2626', Izin: '#1d4ed8', Sakit: '#d97706', Cuti: '#6d28d9' };
    return map[status] || '#64748b';
  };

  // Tanggal per hari dalam bulan ini (YYYY-MM-DD)
  const dateStr = (d) => `${bulan}-${String(d).padStart(2, '0')}`;

  return (
    <div className="table-wrapper" style={{ marginBottom: 20 }}>
      <h4 style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--gray-500)', marginBottom: 8 }}>
        Roster Harian — {new Date(bulan + '-01').toLocaleDateString('id-ID', { year: 'numeric', month: 'long' })}
      </h4>
      <div style={{ display: 'flex', gap: 16, marginBottom: 10, flexWrap: 'wrap', fontSize: '0.72rem', color: 'var(--gray-400)' }}>
        <span style={{ color: '#16a34a', fontWeight: 600 }}>✓ Hadir</span>
        <span style={{ color: '#d97706', fontWeight: 600 }}>T Telat</span>
        <span style={{ color: '#dc2626', fontWeight: 600 }}>A Alpha</span>
        <span style={{ color: '#1d4ed8', fontWeight: 600 }}>I Izin</span>
        <span style={{ color: '#6d28d9', fontWeight: 600 }}>C Cuti</span>
        <span style={{ color: '#d97706', fontWeight: 600 }}>S Sakit</span>
        <span>− Tanpa absen</span>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table className="data-table roster-table" style={{ fontSize: '0.7rem' }}>
          <thead>
            <tr>
              <th style={{ minWidth: 150, position: 'sticky', left: 0, background: '#fff', zIndex: 1 }}>Karyawan</th>
              {days.map(d => (
                <th key={d} style={{ textAlign: 'center', minWidth: 30, padding: '6px 2px' }}>{d}</th>
              ))}
              <th style={{ textAlign: 'center' }}>H</th>
              <th style={{ textAlign: 'center' }}>A</th>
            </tr>
          </thead>
          <tbody>
            {karyawan.map(k => {
              const daysMap = k.days || {};
              return (
                <tr key={k.karyawan_id}>
                  <td style={{ position: 'sticky', left: 0, background: '#fff', fontWeight: 600, whiteSpace: 'nowrap' }}>
                    {k.nama}
                    <span style={{ display: 'block', fontSize: '0.62rem', color: 'var(--gray-400)', fontWeight: 400 }}>{k.nik}</span>
                  </td>
                  {days.map(d => {
                    const day = daysMap[dateStr(d)];
                    if (!day) {
                      return <td key={d} style={{ textAlign: 'center', color: '#cbd5e1' }}>−</td>;
                    }
                    const isTelat = day.telat_menit > 0;
                    const sym = isTelat && day.status === 'Hadir' ? 'T' : symOf(day.status);
                    return (
                      <td
                        key={d}
                        title={`${k.nama} — ${dateStr(d)}\nStatus: ${day.status}${isTelat ? ` (telat ${day.telat_menit} mnt)` : ''}\nMasuk: ${day.check_in || '-'}${day.check_out ? ` | Pulang: ${day.check_out}` : ''}${day.jam_kerja != null ? ` | Kerja: ${fmtJamKerja(day.jam_kerja)}` : ''}`}
                        style={{ textAlign: 'center', padding: '3px 2px', cursor: 'pointer' }}
                        onClick={() => onOpenDetail && onOpenDetail({ ...day, nama: k.nama, nik: k.nik, tanggal: dateStr(d), karyawan_id: k.karyawan_id })}
                      >
                        <div style={{ fontWeight: 700, color: colorOf(day.status), fontSize: '0.72rem' }}>{sym}</div>
                        {day.check_in && (
                          <div style={{ fontSize: '0.55rem', color: 'var(--gray-400)', lineHeight: 1 }}>{day.check_in}</div>
                        )}
                      </td>
                    );
                  })}
                  <td style={{ textAlign: 'center', fontWeight: 700, color: '#16a34a' }}>{k.hadir}</td>
                  <td style={{ textAlign: 'center', fontWeight: 700, color: k.alpha > 0 ? '#dc2626' : 'inherit' }}>{k.alpha}</td>
                </tr>
              );
            })}
            {karyawan.length === 0 && (
              <tr><td colSpan={totalDays + 3} className="text-center text-muted" style={{ padding: 40 }}>
                Tidak ada data absensi untuk bulan ini.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ======== Detail Modal Lengkap ========
function DetailAbsensiModal({ item, onClose, onViewPhoto }) {
  if (!item) return null;

  const fmt = (v) => v ?? '-';
  const fieldRow = (label, value, extra) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '7px 0', borderBottom: '1px solid #f1f5f9', fontSize: '0.8rem' }}>
      <span style={{ color: 'var(--gray-400)', whiteSpace: 'nowrap' }}>{label}</span>
      <span style={{ fontWeight: 600, textAlign: 'right' }}>{value}{extra}</span>
    </div>
  );

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-lg" onClick={e => e.stopPropagation()} style={{ maxWidth: 640, maxHeight: '90vh', overflowY: 'auto' }}>
        <div className="modal-header">
          <h3 style={{ fontSize: '1rem' }}>
            Detail Absensi
            <span style={{ fontSize: '0.8rem', color: 'var(--gray-400)', fontWeight: 400, marginLeft: 8 }}>
              {item.nama} — {item.tanggal}
            </span>
          </h3>
          <button className="btn-icon" onClick={onClose}><X size={18} /></button>
        </div>
        <div className="modal-body">
          {/* Foto Masuk & Pulang */}
          <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
            {item.foto_masuk && (
              <div style={{ flex: 1, textAlign: 'center' }}>
                <img src={item.foto_masuk} alt="Foto masuk"
                  onClick={() => onViewPhoto && onViewPhoto(item.foto_masuk, `Foto Check-in — ${item.nama} (${item.tanggal} ${item.check_in || ''})`)}
                  style={{ width: '100%', maxHeight: 180, objectFit: 'cover', borderRadius: 10, cursor: 'pointer', border: '2px solid #2dce89' }}
                />
                <div style={{ fontSize: '0.7rem', color: '#2dce89', fontWeight: 600, marginTop: 4 }}>Foto Check-in</div>
              </div>
            )}
            {item.foto_pulang && (
              <div style={{ flex: 1, textAlign: 'center' }}>
                <img src={item.foto_pulang} alt="Foto pulang"
                  onClick={() => onViewPhoto && onViewPhoto(item.foto_pulang, `Foto Check-out — ${item.nama} (${item.tanggal} ${item.check_out || ''})`)}
                  style={{ width: '100%', maxHeight: 180, objectFit: 'cover', borderRadius: 10, cursor: 'pointer', border: '2px solid #fb6340' }}
                />
                <div style={{ fontSize: '0.7rem', color: '#fb6340', fontWeight: 600, marginTop: 4 }}>Foto Check-out</div>
              </div>
            )}
            {!item.foto_masuk && !item.foto_pulang && (
              <div style={{ flex: 1, padding: 24, textAlign: 'center', background: '#f8fafc', borderRadius: 10, color: 'var(--gray-400)', fontSize: '0.8rem' }}>
                Tidak ada foto
              </div>
            )}
          </div>

          <div style={{ display: 'flex', gap: 16, marginBottom: 8 }}>
            <div style={{ flex: 1 }}>
              <h4 style={{ fontSize: '0.78rem', fontWeight: 700, color: '#2dce89', marginBottom: 4 }}>CHECK-IN</h4>
              {fieldRow('Jam', fmt(item.check_in))}
              {fieldRow('Shift', fmt(item.shift))}
              {fieldRow('Jadwal masuk', fmt(item.jam_jadwal_masuk))}
              {fieldRow('Koordinat', item.latitude_masuk ? `${item.latitude_masuk?.toFixed(5)}, ${item.longitude_masuk?.toFixed(5)}` : '-')}
              {fieldRow('Jarak radar', fmtJarak(item.jarak_masuk))}
              {fieldRow('Area', <AreaBadge area={item.area_masuk} />)}
              {fieldRow('Alamat', item.alamat_masuk ? <span style={{ maxWidth: 200, display: 'inline-block' }}>{item.alamat_masuk}</span> : '-')}
            </div>
            <div style={{ flex: 1 }}>
              <h4 style={{ fontSize: '0.78rem', fontWeight: 700, color: '#fb6340', marginBottom: 4 }}>CHECK-OUT</h4>
              {fieldRow('Jam', fmt(item.check_out))}
              {fieldRow('Jadwal pulang', fmt(item.jam_jadwal_pulang))}
              {fieldRow('Koordinat', item.latitude_pulang ? `${item.latitude_pulang?.toFixed(5)}, ${item.longitude_pulang?.toFixed(5)}` : '-')}
              {fieldRow('Jarak radar', fmtJarak(item.jarak_pulang))}
              {fieldRow('Area', <AreaBadge area={item.area_pulang} />)}
              {fieldRow('Alamat', item.alamat_pulang ? <span style={{ maxWidth: 200, display: 'inline-block' }}>{item.alamat_pulang}</span> : '-')}
            </div>
          </div>

          <div style={{ background: '#f8fafc', borderRadius: 10, padding: '4px 14px', marginBottom: 8 }}>
            {fieldRow('Status', <StatusBadge status={item.status} />)}
            {fieldRow('Telat', item.telat_menit ? <span style={{ color: '#d97706' }}>{item.telat_menit} menit</span> : (item.check_in ? 'Tepat waktu' : '-'))}
            {fieldRow('Pulang cepat', item.pulang_cepat_menit ? `${item.pulang_cepat_menit} menit` : (item.check_out ? 'Tidak' : '-'))}
            {fieldRow('Jam kerja', fmtJamKerja(item.jam_kerja))}
            {fieldRow('Lembur', item.lembur_menit ? `${item.lembur_menit} menit` : '-')}
            {fieldRow('Keterangan', fmt(item.keterangan))}
          </div>

          <div style={{ fontSize: '0.68rem', color: 'var(--gray-400)' }}>
            Dibuat: {item.created_at} · Diupdate: {item.updated_at}
          </div>
        </div>
      </div>
    </div>
  );
}

// ======== Detail Rekap 1 Karyawan (modal) — ala referensi ========
// Header info karyawan + kartu ringkasan + tabel harian (No, Tanggal,
// Masuk, Pulang, Status, Telat, Lokasi, Alasan/Keterangan).
function DetailOrangModal({ item, onClose }) {
  if (!item) return null;
  const days = item.days || {};
  const tanggalList = Object.keys(days).sort((a, b) => (a < b ? 1 : -1)); // terbaru dulu

  const bulanLabel = item._bulan
    ? new Date(item._bulan + '-01').toLocaleDateString('id-ID', { year: 'numeric', month: 'long' })
    : '';

  // Status harian: Terlambat (telat >0) / Tepat Waktu / status lain
  const dayStatus = (day) => {
    if (day.telat_menit > 0) return { label: 'Terlambat', color: '#d97706', bg: '#fffbeb' };
    if (day.status === 'Hadir') return { label: 'Tepat Waktu', color: '#16a34a', bg: '#f0fdf4' };
    const map = { Izin: ['Izin', '#1d4ed8', '#eff6ff'], Sakit: ['Sakit', '#d97706', '#fffbeb'], Cuti: ['Cuti', '#6d28d9', '#f5f3ff'], Alpha: ['Alpha', '#dc2626', '#fef2f2'] };
    const m = map[day.status] || [day.status, '#475569', '#f1f5f9'];
    return { label: m[0], color: m[1], bg: m[2] };
  };

  const summary = [
    { label: 'Jumlah Kehadiran', value: item.hadir ?? 0, color: '#1e3a8a', bg: '#e0e7ff' },
    { label: 'Tepat Waktu', value: item.tepat ?? 0, color: '#16a34a', bg: '#f0fdf4' },
    { label: 'Terlambat', value: item.terlambat ?? 0, color: '#d97706', bg: '#fffbeb' },
    { label: 'Luar Jangkauan', value: item.luar_jangkauan ?? 0, color: '#b91c1c', bg: '#fef2f2' },
  ];

  const fmtTanggal = (tgl) => {
    const d = new Date(tgl + 'T00:00:00');
    return d.toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const infoRow = (label, value) => (
    <div style={{ display: 'flex', gap: 8, padding: '3px 0' }}>
      <span style={{ width: 170, flexShrink: 0, color: '#64748b', fontSize: '0.75rem' }}>{label}</span>
      <span style={{ fontWeight: 600, fontSize: '0.78rem', color: '#0f172a' }}>{value || '-'}</span>
    </div>
  );

  // ===== Export ke PDF (jsPDF + autoTable) =====
  const exportPDF = () => {
    const { jsPDF } = require('jspdf');
    const autoTable = require('jspdf-autotable').default || require('jspdf-autotable');
    const doc = new jsPDF('landscape', 'mm', 'a4');
    const pageW = doc.internal.pageSize.getWidth();
    const margin = 14;

    // Header biru
    doc.setFillColor(29, 78, 216);
    doc.rect(0, 0, pageW, 34, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.text('REKAP ABSENSI KARYAWAN', margin, 12);
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text(`Periode ${bulanLabel}`, margin, 20);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(item.nama || '', margin, 27);

    // Info identitas (2 kolom)
    const info = [
      ['Nama Karyawan', item.nama],
      ['Stambuk (STB)', item.nik],
      ['Divisi', item.bagian],
      ['Sektor', item.jabatan],
      ['Departemen', item.bagian],
      ['Work Shift', item.shift_label],
    ];
    let iy = 42;
    doc.setFontSize(8);
    info.forEach(([l, v], i) => {
      const x = margin + (i % 2) * 95;
      const y = iy + Math.floor(i / 2) * 6;
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(100, 116, 139);
      doc.text(l + ':', x, y);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(15, 23, 42);
      doc.text(String(v || '-'), x + 42, y);
    });

    // Ringkasan stat
    const stats = [
      ['Jumlah Kehadiran', item.hadir ?? 0, '#1e3a8a'],
      ['Tepat Waktu', item.tepat ?? 0, '#16a34a'],
      ['Terlambat', item.terlambat ?? 0, '#d97706'],
      ['Luar Jangkauan', item.luar_jangkauan ?? 0, '#b91c1c'],
    ];
    const statY = 62;
    const statW = (pageW - 2 * margin - 12) / 4;
    stats.forEach(([label, value, color], i) => {
      const x = margin + i * (statW + 4);
      doc.setFillColor(248, 250, 252);
      doc.roundedRect(x, statY, statW, 18, 2, 2, 'F');
      doc.setFillColor(color);
      doc.rect(x, statY, 2.5, 18, 'F');
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7);
      doc.setTextColor(100, 116, 139);
      doc.text(label, x + 6, statY + 7);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(12);
      doc.setTextColor(color);
      doc.text(String(value), x + 6, statY + 15);
    });

    // Tabel harian
    const dayRows = tanggalList.map((tgl, i) => {
      const day = days[tgl];
      const st = dayStatus(day);
      return [
        String(i + 1),
        fmtTanggal(tgl),
        day.check_in || '-',
        day.check_out || '-',
        st.label,
        day.telat_menit > 0 ? `${day.telat_menit} mnt` : '-',
        day.area ? (day.area === 'INSIDE' ? 'Dalam' : 'Luar') : '-',
        day.keterangan || '-',
      ];
    });

    autoTable(doc, {
      head: [['No', 'Tanggal', 'Masuk', 'Pulang', 'Status', 'Telat', 'Lokasi', 'Alasan / Keterangan']],
      body: dayRows,
      startY: statY + 26,
      margin: { left: margin, right: margin },
      styles: { fontSize: 7.5, cellPadding: 2.5, textColor: [51, 65, 85] },
      headStyles: { fillColor: [29, 78, 216], textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 7.5, halign: 'center' },
      columnStyles: {
        0: { halign: 'center', cellWidth: 10 },
        1: { cellWidth: 30 },
        2: { halign: 'center', cellWidth: 18 },
        3: { halign: 'center', cellWidth: 18 },
        4: { cellWidth: 24 },
        5: { halign: 'center', cellWidth: 20 },
        6: { halign: 'center', cellWidth: 16 },
      },
      alternateRowStyles: { fillColor: [248, 250, 252] },
    });

    // Footer
    const finalY = doc.lastAutoTable?.finalY || statY + 60;
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(7);
    doc.setTextColor(148, 163, 184);
    doc.text(`Dicetak pada: ${new Date().toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' })}`, margin, finalY + 12);

    doc.save(`Rekap_Absensi_${item.nik || item.karyawan_id}_${(item._bulan || '').slice(0, 7)}.pdf`);
  };

  // ===== Export ke Excel (CSV dengan BOM agar terbaca Excel) =====
  const exportExcel = () => {
    const rows = [
      ['REKAP ABSENSI KARYAWAN'],
      [`Periode ${bulanLabel}`],
      [],
      ['Nama Karyawan', item.nama || ''],
      ['Stambuk (STB)', item.nik || ''],
      ['Divisi', item.bagian || ''],
      ['Sektor', item.jabatan || ''],
      ['Departemen', item.bagian || ''],
      ['Work Shift', item.shift_label || ''],
      [],
      ['Jumlah Kehadiran', item.hadir ?? 0],
      ['Tepat Waktu', item.tepat ?? 0],
      ['Terlambat', item.terlambat ?? 0],
      ['Luar Jangkauan', item.luar_jangkauan ?? 0],
      [],
      ['No', 'Tanggal', 'Masuk', 'Pulang', 'Status', 'Telat', 'Lokasi', 'Alasan / Keterangan'],
      ...tanggalList.map((tgl, i) => {
        const day = days[tgl];
        return [
          String(i + 1),
          fmtTanggal(tgl),
          day.check_in || '',
          day.check_out || '',
          dayStatus(day).label,
          day.telat_menit > 0 ? `${day.telat_menit} mnt` : '',
          day.area ? (day.area === 'INSIDE' ? 'Dalam' : 'Luar') : '',
          day.keterangan || '',
        ];
      }),
    ];
    const csv = rows.map(r => r.map(c => `"${String(c ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `Rekap_Absensi_${item.nik || item.karyawan_id}_${(item._bulan || '').slice(0, 7)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-lg" onClick={e => e.stopPropagation()} style={{ maxWidth: 820, maxHeight: '92vh', overflowY: 'auto', padding: 0, overflow: 'hidden' }}>
        {/* Header biru ala referensi */}
        <div style={{ background: '#1d4ed8', padding: '18px 24px', color: '#fff' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: 1, opacity: 0.85 }}>Rekap Absensi Karyawan</div>
              <div style={{ fontSize: '1.15rem', fontWeight: 700, marginTop: 2 }}>Periode {bulanLabel}</div>
              <div style={{ fontSize: '0.9rem', opacity: 0.95, marginTop: 4 }}>{item.nama}</div>
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <button
                onClick={exportPDF}
                title="Cetak ke PDF"
                style={{ background: 'rgba(255,255,255,0.18)', color: '#fff', border: 'none', borderRadius: 8, padding: '7px 12px', cursor: 'pointer', fontSize: '0.75rem', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 5, transition: 'all 0.15s' }}
                onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.3)'}
                onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.18)'}
              >
                <Printer size={14} /> PDF
              </button>
              <button
                onClick={exportExcel}
                title="Export ke Excel"
                style={{ background: 'rgba(255,255,255,0.18)', color: '#fff', border: 'none', borderRadius: 8, padding: '7px 12px', cursor: 'pointer', fontSize: '0.75rem', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 5, transition: 'all 0.15s' }}
                onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.3)'}
                onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.18)'}
              >
                <Download size={14} /> Excel
              </button>
              <button className="btn-icon" onClick={onClose} style={{ color: '#fff', background: 'rgba(255,255,255,0.15)', borderRadius: '50%', width: 32, height: 32, border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <X size={18} />
              </button>
            </div>
          </div>
        </div>

        <div style={{ padding: '16px 24px' }}>
          {/* Foto + info identitas */}
          <div style={{ display: 'flex', gap: 16, alignItems: 'center', marginBottom: 16 }}>
            {item.foto_url ? (
              <img src={item.foto_url} alt={item.nama}
                style={{ width: 64, height: 64, borderRadius: '50%', objectFit: 'cover', border: '2px solid #e2e8f0' }}
              />
            ) : (
              <div style={{ width: 64, height: 64, borderRadius: '50%', background: 'var(--accent-tint-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--gray-400)' }}>
                <User size={28} />
              </div>
            )}
            <div style={{ flex: 1 }}>
              {infoRow('Nama Karyawan', item.nama)}
              {infoRow('Stambuk (STB)', item.nik)}
              {infoRow('Divisi', item.bagian)}
              {infoRow('Sektor', item.jabatan)}
              {infoRow('Departemen', item.bagian)}
              {infoRow('Work Shift', item.shift_label)}
            </div>
          </div>

          {/* Kartu ringkasan */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 10, marginBottom: 16 }}>
            {summary.map(s => (
              <div key={s.label} style={{ background: s.bg, borderRadius: 10, padding: '12px 10px', textAlign: 'center', border: '1px solid rgba(0,0,0,0.04)' }}>
                <div style={{ fontSize: '1.35rem', fontWeight: 800, color: s.color, lineHeight: 1.1 }}>{s.value}</div>
                <div style={{ fontSize: '0.68rem', color: '#475569', fontWeight: 600, marginTop: 3 }}>{s.label}</div>
              </div>
            ))}
          </div>

          {/* Tabel harian */}
          <div className="table-wrapper">
            <table className="data-table" style={{ fontSize: '0.78rem' }}>
              <thead>
                <tr>
                  <th style={{ width: 36 }}>No</th>
                  <th>Tanggal</th>
                  <th>Masuk</th>
                  <th>Pulang</th>
                  <th>Status</th>
                  <th>Telat</th>
                  <th>Lokasi</th>
                  <th>Alasan / Keterangan</th>
                </tr>
              </thead>
              <tbody>
                {tanggalList.map((tgl, i) => {
                  const day = days[tgl];
                  const st = dayStatus(day);
                  return (
                    <tr key={tgl}>
                      <td className="text-muted">{i + 1}</td>
                      <td>{fmtTanggal(tgl)}</td>
                      <td style={{ fontWeight: 600 }}>{day.check_in || '-'}</td>
                      <td>{day.check_out || '-'}</td>
                      <td>
                        <span className="badge" style={{ background: st.bg, color: st.color, whiteSpace: 'nowrap' }}>{st.label}</span>
                      </td>
                      <td style={{ color: day.telat_menit > 0 ? '#d97706' : 'var(--gray-400)', fontWeight: day.telat_menit > 0 ? 600 : 400 }}>
                        {day.telat_menit > 0 ? `${day.telat_menit} mnt` : '-'}
                      </td>
                      <td>
                        {day.area ? (
                          <span className="badge" style={{
                            background: day.area === 'INSIDE' ? '#d1fae5' : '#fee2e2',
                            color: day.area === 'INSIDE' ? '#065f46' : '#991b1b',
                            whiteSpace: 'nowrap',
                          }}>
                            {day.area === 'INSIDE' ? 'Dalam' : 'Luar'}
                          </span>
                        ) : <span className="text-muted">-</span>}
                      </td>
                      <td style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>{day.keterangan || '-'}</td>
                    </tr>
                  );
                })}
                {tanggalList.length === 0 && (
                  <tr><td colSpan={8} className="text-center text-muted" style={{ padding: 30 }}>
                    Tidak ada catatan absensi pada bulan ini.
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

// ======== Main Page ========
export default function Absensi() {
  const { hasMenuAction } = useAuth();
  const [data, setData] = useState([]);
  const [karyawan, setKaryawan] = useState([]);
  const [stats, setStats] = useState(null);
  const [, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('hariIni'); // 'hariIni' | 'rekapOrang' | 'rekap'
  // Rekap/Orang — rekap bulanan per karyawan
  const [bagianList, setBagianList] = useState([]);
  const [rekapOrangBulan, setRekapOrangBulan] = useState(todayStr().slice(0, 7)); // YYYY-MM
  const [rekapOrangBagian, setRekapOrangBagian] = useState('');
  const [rekapOrangNama, setRekapOrangNama] = useState('');
  const [rekapOrangStatus, setRekapOrangStatus] = useState('');
  const [rekapOrangData, setRekapOrangData] = useState(null);
  const [rekapOrangLoading, setRekapOrangLoading] = useState(false);
  const [detailOrang, setDetailOrang] = useState(null); // { item, days } untuk modal detail 1 karyawan
  const [photoViewer, setPhotoViewer] = useState(null); // { url, label }
  const [detailItem, setDetailItem] = useState(null); // absensi detail modal
  const [rekapBulan, setRekapBulan] = useState(todayStr().slice(0, 7));
  const [rekapData, setRekapData] = useState(null);
  const [rekapLoading, setRekapLoading] = useState(false);
  const [rekapSearch, setRekapSearch] = useState('');
  const [rekapView, setRekapView] = useState('ringkasan'); // 'ringkasan' | 'roster'
  
  // Camera & GPS state
  const [showCamera, setShowCamera] = useState(false);
  const [cameraMode, setCameraMode] = useState('checkin'); // 'checkin' | 'checkout'
  const [capturedPhoto, setCapturedPhoto] = useState(null);
  const [checkinTarget, setCheckinTarget] = useState(null); // { karyawan_id, nama }
  const [gpsReady, setGpsReady] = useState(false);
  const [gpsLat, setGpsLat] = useState(null);
  const [gpsLng, setGpsLng] = useState(null);
  const [gpsAddr, setGpsAddr] = useState('');
  const [checkinLoading, setCheckinLoading] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      getAbsensi({ tanggal: todayStr() }),
      getKaryawan(),
      getAbsensiStats(),
    ])
      .then(([absRes, karRes, statsRes]) => {
        setData(absRes.data);
        setKaryawan(karRes.data.filter(k => k.status === 'Aktif'));
        setStats(statsRes.data);
      })
      .catch(err => console.error(err))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  // ===== Rekap/Orang: muat daftar bagian (untuk filter) =====
  useEffect(() => {
    getBagian()
      .then(res => setBagianList(res.data || []))
      .catch(() => {});
  }, []);

  // ===== Check-in / Check-out with Camera + GPS =====
  const handleCheckin = (karyawanId, nama) => {
    setCheckinTarget({ karyawan_id: karyawanId, nama });
    setCameraMode('checkin');
    setCapturedPhoto(null);
    setGpsReady(false);
    setGpsLat(null);
    setGpsLng(null);
    setGpsAddr('');
    setShowCamera(true);
  };

  const handleCheckout = (karyawanId, nama) => {
    setCheckinTarget({ karyawan_id: karyawanId, nama });
    setCameraMode('checkout');
    setCapturedPhoto(null);
    setGpsReady(false);
    setGpsLat(null);
    setGpsLng(null);
    setGpsAddr('');
    setShowCamera(true);
  };

  const handleCameraCapture = (dataUrl) => {
    setCapturedPhoto(dataUrl);
    setShowCamera(false);
    // Mulai GPS setelah kamera selesai
  };

  const handleGpsReady = (lat, lng, addr) => {
    setGpsLat(lat);
    setGpsLng(lng);
    setGpsAddr(addr || '');
    setGpsReady(true);
  };

  const submitCheckin = async () => {
    if (!capturedPhoto) {
      alert('WAJIB ambil foto selfie terlebih dahulu!');
      setShowCamera(true);
      return;
    }
    if (!checkinTarget) return;
    setCheckinLoading(true);
    try {
      await absensiCheckin({
        karyawan_id: checkinTarget.karyawan_id,
        status: 'Hadir',
        foto_masuk: capturedPhoto,
        latitude: gpsLat,
        longitude: gpsLng,
        alamat: gpsAddr || '',
      });
      setCheckinTarget(null);
      setCapturedPhoto(null);
      load();
    } catch (err) {
      alert(err.response?.data?.error || 'Gagal check-in');
    } finally {
      setCheckinLoading(false);
    }
  };

  const submitCheckout = async () => {
    if (!capturedPhoto) {
      alert('WAJIB ambil foto selfie terlebih dahulu!');
      setShowCamera(true);
      return;
    }
    if (!checkinTarget) return;
    setCheckinLoading(true);
    try {
      await absensiCheckout({
        karyawan_id: checkinTarget.karyawan_id,
        foto_pulang: capturedPhoto,
        latitude: gpsLat,
        longitude: gpsLng,
        alamat: gpsAddr || '',
      });
      setCheckinTarget(null);
      setCapturedPhoto(null);
      load();
    } catch (err) {
      alert(err.response?.data?.error || 'Gagal check-out');
    } finally {
      setCheckinLoading(false);
    }
  };

  const cancelCheckin = () => {
    setCheckinTarget(null);
    setCapturedPhoto(null);
    setShowCamera(false);
    setGpsReady(false);
  };

  // ===== CRUD Modal =====
  const openCreate = () => {
    setEditing(null);
    setForm({ ...emptyForm, tanggal: todayStr(), status: 'Hadir' });
    setError(''); setShowModal(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setForm({
      karyawan_id: item.karyawan_id,
      tanggal: item.tanggal,
      check_in: item.check_in || '',
      check_out: item.check_out || '',
      status: item.status || 'Hadir',
      keterangan: item.keterangan || '',
    });
    setError(''); setShowModal(true);
  };

  const handleSave = async () => {
    setError('');
    if (!form.karyawan_id) { setError('Pilih karyawan'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        karyawan_id: Number(form.karyawan_id),
      };
      if (editing) {
        await updateAbsensi(editing.id, payload);
      } else {
        await createAbsensi(payload);
      }
      setShowModal(false);
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Gagal menyimpan');
    } finally { setSaving(false); }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Yakin ingin menghapus absensi ${item.nama} tanggal ${item.tanggal}?`)) return;
    try { await deleteAbsensi(item.id); load(); }
    catch (err) { alert(err.response?.data?.error || 'Gagal menghapus'); }
  };

  // ===== Rekap Bulanan =====
  useEffect(() => {
    if (activeTab !== 'rekap') return;
    setRekapLoading(true);
    getAbsensiRekap({ bulan: rekapBulan })
      .then(res => setRekapData(res.data))
      .catch(err => console.error(err))
      .finally(() => setRekapLoading(false));
  }, [activeTab, rekapBulan]);

  // ===== Rekap/Orang: load per karyawan untuk bulan terpilih =====
  const loadRekapOrang = useCallback(() => {
    if (!rekapOrangBulan) return;
    setRekapOrangLoading(true);
    getAbsensiRekapOrang({
      bulan: rekapOrangBulan,
      bagian_id: rekapOrangBagian || undefined,
      nama: rekapOrangNama || undefined,
      status: rekapOrangStatus || undefined,
    })
      .then(res => setRekapOrangData(res.data))
      .catch(err => console.error(err))
      .finally(() => setRekapOrangLoading(false));
  }, [rekapOrangBulan, rekapOrangBagian, rekapOrangNama, rekapOrangStatus]);

  // Auto-load saat tab aktif atau filter berubah
  useEffect(() => {
    if (activeTab === 'rekapOrang') {
      loadRekapOrang();
    }
  }, [activeTab, loadRekapOrang]);

  // ===== PDF Export =====
  const generateRekapPDF = () => {
    if (!rekapData) return;
    const { jsPDF } = require('jspdf');
    const autoTable = require('jspdf-autotable').default || require('jspdf-autotable');

    const doc = new jsPDF('landscape', 'mm', 'a4');
    const pageW = doc.internal.pageSize.getWidth();
    const margin = 15;

    // Colors
    const primary = '#11cdef';
    const dark = '#172b4d';
    const gray = '#8898aa';

    // === Header ===
    doc.setFillColor(primary);
    doc.rect(0, 0, pageW, 38, 'F');

    doc.setTextColor(255, 255, 255);
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text('REKAP ABSENSI BULANAN', margin, 18);

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    const bulanLabel = rekapBulan ? new Date(rekapBulan + '-01').toLocaleDateString('id-ID', { year: 'numeric', month: 'long' }) : '';
    doc.text(`Periode: ${bulanLabel}`, margin, 28);

    // Company name on right
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('Layanan Synexa', pageW - margin, 18, { align: 'right' });
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.text('Sistem Informasi Armada', pageW - margin, 25, { align: 'right' });

    // === Summary Stats ===
    const startY = 50;
    doc.setDrawColor(230, 230, 230);
    doc.setFillColor(248, 249, 252);
    doc.roundedRect(margin, startY, pageW - 2 * margin, 28, 3, 3, 'FD');

    doc.setTextColor(dark);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');

    // Draw stat boxes
    const stats = [
      { label: 'Hari Kerja', value: totalHari.toString(), color: '#11cdef' },
      { label: 'Total Hadir', value: totalHadirRekap.toString(), color: '#2dce89' },
      { label: 'Total Alpha', value: totalAlphaRekap.toString(), color: '#f5365c' },
      { label: '% Kehadiran', value: (totalHadirRekap + totalAbsenRekap > 0 ? Math.round(totalHadirRekap / (totalHadirRekap + totalAbsenRekap) * 100) : 0) + '%', color: '#fb6340' },
    ];

    const statW = (pageW - 2 * margin - 20) / 4;
    stats.forEach((s, i) => {
      const x = margin + i * (statW + 6) + 5;
      doc.setFillColor(s.color);
      doc.rect(x, startY + 5, 3, 18, 'F');

      doc.setTextColor(gray);
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.text(s.label, x + 8, startY + 12);

      doc.setTextColor(dark);
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text(s.value, x + 8, startY + 24);
    });

    // === Employee Table ===
    const tableTop = startY + 40;

    const headers = [['No', 'NIK', 'Nama Karyawan', 'Hadir', 'Izin', 'Sakit', 'Cuti', 'Alpha', 'Total', '% Hadir']];
    const rows = rekapData.karyawan.map((k, i) => {
      const pct = k.total > 0 ? Math.round(k.hadir / k.total * 100) : 0;
      return [
        (i + 1).toString(),
        k.nik,
        k.nama,
        k.hadir.toString(),
        k.izin.toString(),
        k.sakit.toString(),
        k.cuti.toString(),
        k.alpha.toString(),
        k.total.toString(),
        pct + '%',
      ];
    });

    // Summary row
    const totalHadir = rekapData.karyawan.reduce((s, k) => s + k.hadir, 0);
    const totalIzin = rekapData.karyawan.reduce((s, k) => s + k.izin, 0);
    const totalSakit = rekapData.karyawan.reduce((s, k) => s + k.sakit, 0);
    const totalCuti = rekapData.karyawan.reduce((s, k) => s + k.cuti, 0);
    const totalAlpha = rekapData.karyawan.reduce((s, k) => s + k.alpha, 0);
    const totalSemua = rekapData.karyawan.reduce((s, k) => s + k.total, 0);
    const pctAll = totalSemua > 0 ? Math.round(totalHadir / totalSemua * 100) : 0;
    const footerRow = ['', '', 'TOTAL', totalHadir.toString(), totalIzin.toString(), totalSakit.toString(), totalCuti.toString(), totalAlpha.toString(), totalSemua.toString(), pctAll + '%'];

    autoTable(doc, {
      head: headers,
      body: [...rows, footerRow],
      startY: tableTop,
      margin: { left: margin, right: margin },
      styles: {
        fontSize: 8,
        cellPadding: 3,
        lineColor: [220, 220, 220],
        lineWidth: 0.5,
        textColor: [50, 50, 50],
      },
      headStyles: {
        fillColor: [94, 114, 228],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        fontSize: 8,
        halign: 'center',
      },
      bodyStyles: {
        fontSize: 8,
      },
      columnStyles: {
        0: { halign: 'center', cellWidth: 12 },
        1: { halign: 'center', cellWidth: 30 },
        3: { halign: 'center', cellWidth: 16, textColor: [45, 206, 137] },
        4: { halign: 'center', cellWidth: 16, textColor: [94, 114, 228] },
        5: { halign: 'center', cellWidth: 16, textColor: [251, 99, 64] },
        6: { halign: 'center', cellWidth: 16, textColor: [137, 101, 224] },
        7: { halign: 'center', cellWidth: 16, textColor: [245, 54, 92] },
        8: { halign: 'center', cellWidth: 16 },
        9: { halign: 'center', cellWidth: 18 },
      },
      footStyles: {
        fillColor: [248, 249, 252],
        textColor: [23, 43, 77],
        fontStyle: 'bold',
        fontSize: 8,
      },
    });

    // === Footer ===
    const finalY = doc.lastAutoTable.finalY || startY + 100;
    const footerY = finalY + 20;

    if (footerY < 260) {
      doc.setDrawColor(200, 200, 200);
      doc.line(margin, footerY, pageW - margin, footerY);

      doc.setTextColor(gray);
      doc.setFontSize(7);
      doc.setFont('helvetica', 'italic');
      const now = new Date();
      const dateStr = now.toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' });
      doc.text(`Dicetak pada: ${dateStr}`, margin, footerY + 8);

      // Signature
      doc.text('Mengetahui,', pageW - margin - 50, footerY - 10);
      doc.text('__________________________', pageW - margin - 50, footerY + 15);
      doc.setFont('helvetica', 'bold');
      doc.text('Kepala Bagian', pageW - margin - 50, footerY + 22);
    }

    // Save
    const filename = `Rekap_Absensi_${rekapBulan || 'bulanan'}.pdf`;
    doc.save(filename);
  };

  // ===== Chart Helpers =====
  const STATUS_COLORS = { Hadir: '#2dce89', Izin: '#11cdef', Sakit: '#fb6340', Cuti: '#11cdef', Alpha: '#f5365c' };
  const STATUS_KEYS = ['Hadir', 'Izin', 'Sakit', 'Cuti', 'Alpha'];

  const sumRekap = (key) => rekapData?.daily?.reduce((s, d) => s + (d[key] || 0), 0) || 0;
  const totalHari = rekapData?.daily?.length || 0;
  const totalHadirRekap = sumRekap('hadir');
  const totalAlphaRekap = sumRekap('alpha');
  const totalAbsenRekap = sumRekap('izin') + sumRekap('sakit') + sumRekap('cuti') + totalAlphaRekap;

  const filteredKaryawan = (rekapData?.karyawan || []).filter(k =>
    !rekapSearch || k.nama?.toLowerCase().includes(rekapSearch.toLowerCase()) ||
    k.nik?.toLowerCase().includes(rekapSearch.toLowerCase())
  );

  return (
    <div className="page">
      <div className="page-header">
        <div className="page-header-row">
          <div>
            <h2>Absensi Karyawan</h2>
            <p className="page-desc">{stats?.karyawan_aktif || 0} karyawan aktif</p>
          </div>
          <div className="page-header-actions">
            {hasMenuAction('absensi', 'create') && (
              <button className="btn btn-primary" onClick={openCreate}>
                <Plus size={16} /><span>Tambah Absensi</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="dash-stats-row" style={{ marginBottom: 20 }}>
          <div className="dash-stat-col">
            <StatCard icon={CheckCircle} label="Hadir Hari Ini" value={stats.hari_ini?.hadir} color="#2dce89"
              sublabel={`${stats.hari_ini?.checkin || 0} sudah check-in`} />
          </div>
          <div className="dash-stat-col">
            <StatCard icon={Clock} label="Check-in" value={stats.hari_ini?.checkin} color="#11cdef"
              sublabel={`${stats.hari_ini?.checkout || 0} sudah check-out`} />
          </div>
          <div className="dash-stat-col">
            <StatCard icon={AlertCircle} label="Total Hari Ini" value={stats.hari_ini?.total} color="#11cdef"
              sublabel={`${stats.karyawan_aktif - (stats.hari_ini?.total || 0)} belum absen`} />
          </div>
          <div className="dash-stat-col">
            <StatCard icon={User} label="Bulan Ini" value={stats.total} color="#fb6340"
              sublabel={`${stats.bulan_ini?.length || 0} jenis status`} />
          </div>
        </div>
      )}

      {/* Tab Navigation */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
        <button
          onClick={() => setActiveTab('hariIni')}
          className={`btn ${activeTab === 'hariIni' ? 'btn-primary' : 'btn-secondary'}`}
        >
          <CheckCircle size={16} /> Hari Ini
        </button>
        <button
          onClick={() => setActiveTab('rekapOrang')}
          className={`btn ${activeTab === 'rekapOrang' ? 'btn-primary' : 'btn-secondary'}`}
        >
          <User size={16} /> Rekap/Orang
        </button>
        <button
          onClick={() => setActiveTab('rekap')}
          className={`btn ${activeTab === 'rekap' ? 'btn-primary' : 'btn-secondary'}`}
        >
          <BarChart3 size={16} /> Rekap Bulanan
        </button>
      </div>

      {/* Tab: Hari Ini — Quick Check-in/out Cards */}
      {activeTab === 'hariIni' && (
        <div>
          {/* Karyawan without today's attendance — Quick Check-in */}
          {karyawan.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <h4 style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--gray-500)', marginBottom: 8 }}>
                Karyawan Belum Absen Hari Ini
              </h4>
              <div className="regions-grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))' }}>
                {karyawan.filter(k => !data.find(a => a.karyawan_id === k.id)).map(k => (
                  <div key={k.id} className="part-card" style={{ padding: 14 }}>
                    <div className="part-icon" style={{ background: 'var(--accent-tint-bg)', color: 'var(--info)' }}>
                      <User size={20} />
                    </div>
                    <div className="part-info">
                      <h4 style={{ fontSize: '0.82rem' }}>{k.nama}</h4>
                      <span className="part-code">{k.nik}</span>
                    </div>
                    {hasMenuAction('absensi', 'create') && (
                      <button className="btn btn-primary" style={{ padding: '6px 12px', fontSize: '0.75rem' }}
                        onClick={() => handleCheckin(k.id, k.nama)}>
                        <LogIn size={14} /> Check-in
                      </button>
                    )}
                  </div>
                ))}
                {karyawan.length > 0 && data.length >= karyawan.length && (
                  <div className="page-loading" style={{ gridColumn: '1/-1', padding: 20 }}>
                    <CheckCircle size={24} color="#2dce89" />
                    <p style={{ marginTop: 8, color: '#065f46', fontWeight: 600 }}>Semua karyawan sudah absen hari ini!</p>
                  </div>
                )}
                {karyawan.length === 0 && (
                  <div className="page-loading" style={{ gridColumn: '1/-1', padding: 20 }}>
                    <p className="text-muted">Tidak ada karyawan aktif.</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Today's attendance list */}
          <h4 style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--gray-500)', marginBottom: 8 }}>
            Absensi Hari Ini ({todayStr()})
          </h4>
          <div className="table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th>NIK</th><th>Nama</th><th>Check-in</th><th>Check-out</th><th>Status</th><th>Keterangan</th>
                  <th style={{ width: 120 }}>Aksi</th>
                </tr>
              </thead>
              <tbody>
                {data.map(d => (
                  <tr key={d.id}>
                    <td><code>{d.nik || '-'}</code></td>
                    <td><strong>{d.nama}</strong></td>
                    <td>
                      {d.check_in ? (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: '#2dce89', fontWeight: 600 }}>
                          <LogIn size={14} /> {d.check_in}
                          {d.foto_masuk && (
                            <span title="Ada foto check-in" style={{ display:'inline-flex', alignItems:'center', color:'#2dce89' }}>
                              <Camera size={12} />
                            </span>
                          )}
                          {d.latitude_masuk && (
                            <span title={d.alamat_masuk || `Lat: ${d.latitude_masuk}, Lng: ${d.longitude_masuk}`} style={{ display:'inline-flex', alignItems:'center', color:'#11cdef' }}>
                              <MapPin size={12} />
                            </span>
                          )}
                        </span>
                      ) : <span className="text-muted">-</span>}
                    </td>
                    <td>
                      {d.check_out ? (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: '#fb6340', fontWeight: 600 }}>
                          <LogOut size={14} /> {d.check_out}
                          {d.foto_pulang && (
                            <span title="Ada foto check-out" style={{ display:'inline-flex', alignItems:'center', color:'#fb6340' }}>
                              <Camera size={12} />
                            </span>
                          )}
                          {d.latitude_pulang && (
                            <span title={d.alamat_pulang || `Lat: ${d.latitude_pulang}, Lng: ${d.longitude_pulang}`} style={{ display:'inline-flex', alignItems:'center', color:'#11cdef' }}>
                              <MapPin size={12} />
                            </span>
                          )}
                        </span>
                      ) : d.check_in ? (
                        hasMenuAction('absensi', 'edit') && (
                          <button className="btn btn-secondary" style={{ padding: '3px 10px', fontSize: '0.75rem' }}
                            onClick={() => handleCheckout(d.karyawan_id, d.nama)}>
                            <LogOut size={12} /> Check-out
                          </button>
                        )
                      ) : <span className="text-muted">-</span>}
                    </td>
                    <td><StatusBadge status={d.status} /></td>
                    <td style={{ fontSize: '0.8rem', color: 'var(--gray-400)' }}>{d.keterangan || '-'}</td>
                    <td>
                      <div className="action-btns">
                        <button className="btn-icon" title="Detail lengkap" onClick={() => setDetailItem(d)}
                          style={{ color: '#11cdef' }}>
                          <Eye size={14} />
                        </button>
                        {hasMenuAction('absensi', 'edit') && (
                          <button className="btn-icon" title="Edit" onClick={() => openEdit(d)}><Edit2 size={14} /></button>
                        )}
                        {hasMenuAction('absensi', 'delete') && (
                          <button className="btn-icon btn-danger" title="Hapus" onClick={() => handleDelete(d)}><Trash2 size={14} /></button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {data.length === 0 && (
                  <tr><td colSpan={7} className="text-center text-muted" style={{ padding: 40 }}>
                    Belum ada data absensi untuk hari ini.
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Tab: Rekap/Orang — rekap bulanan per karyawan */}
      {activeTab === 'rekapOrang' && (
        <div>
          {/* Filter: Bulan+Tahun, Bagian, Nama, Status */}
          <div className="filters" style={{ flexWrap: 'wrap', marginBottom: 20 }}>
            <div className="search-box">
              <Calendar size={18} />
              <input
                type="month"
                value={rekapOrangBulan}
                onChange={e => setRekapOrangBulan(e.target.value)}
                style={{ border: 'none', outline: 'none', fontSize: '0.9rem', fontWeight: 600, background: 'transparent' }}
              />
            </div>
            <select value={rekapOrangBagian} onChange={e => setRekapOrangBagian(e.target.value)} style={{ maxWidth: 180 }}>
              <option value="">Semua Bagian</option>
              {bagianList.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
            <div className="search-box">
              <Search size={18} />
              <input
                type="text"
                placeholder="Nama karyawan..."
                value={rekapOrangNama}
                onChange={e => setRekapOrangNama(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') loadRekapOrang(); }}
              />
            </div>
            <select value={rekapOrangStatus} onChange={e => setRekapOrangStatus(e.target.value)}>
              <option value="">Semua Status</option>
              {STATUS_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
            <button className="btn btn-primary" onClick={loadRekapOrang} style={{ padding: '8px 16px' }}>
              <Search size={15} /> Tampilkan
            </button>
          </div>

          {rekapOrangLoading ? (
            <div className="page-loading"><div className="spinner-lg" /><p>Memuat rekap...</p></div>
          ) : rekapOrangData ? (
            <>
              {/* Ringkasan singkat */}
              <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
                <span className="badge" style={{ background: '#e0e7ff', color: '#1e3a8a' }}>
                  {rekapOrangData.total} karyawan
                </span>
                <span className="badge" style={{ background: '#d1fae5', color: '#065f46' }}>
                  Total Hadir: {rekapOrangData.data.reduce((s, d) => s + (d.hadir || 0), 0)}
                </span>
                <span className="badge" style={{ background: '#fef3c7', color: '#92400e' }}>
                  Terlambat: {rekapOrangData.data.reduce((s, d) => s + (d.terlambat || 0), 0)}
                </span>
                <span className="badge" style={{ background: '#fee2e2', color: '#991b1b' }}>
                  Alpha: {rekapOrangData.data.reduce((s, d) => s + (d.alpha || 0), 0)}
                </span>
              </div>

              <div className="table-wrapper">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th style={{ width: 40 }}>No</th>
                      <th style={{ width: 56 }}>Foto</th>
                      <th>ID Karyawan</th>
                      <th>Nama Karyawan</th>
                      <th>Bagian</th>
                      <th style={{ textAlign: 'center', color: '#2dce89' }}>Hadir</th>
                      <th style={{ textAlign: 'center', color: '#065f46' }}>Tepat</th>
                      <th style={{ textAlign: 'center', color: '#d97706' }}>Terlambat</th>
                      <th style={{ width: 90, textAlign: 'center' }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rekapOrangData.data.map((d, i) => (
                      <tr key={d.karyawan_id}>
                        <td className="text-muted" style={{ fontSize: '0.78rem' }}>{i + 1}</td>
                        <td>
                          {d.foto_url ? (
                            <img
                              src={d.foto_url}
                              alt={d.nama}
                              title={d.nama}
                              style={{ width: 40, height: 40, borderRadius: '50%', objectFit: 'cover', border: '2px solid #e2e8f0', cursor: 'pointer' }}
                              onClick={() => setPhotoViewer({ url: d.foto_url, label: `Foto Profil — ${d.nama}` })}
                            />
                          ) : (
                            <div style={{
                              width: 40, height: 40, borderRadius: '50%', background: 'var(--accent-tint-bg)',
                              display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--gray-400)'
                            }}>
                              <User size={18} />
                            </div>
                          )}
                        </td>
                        <td><code>{d.nik || '-'}</code></td>
                        <td><strong>{d.nama}</strong></td>
                        <td style={{ fontSize: '0.8rem' }}>{d.bagian || '-'}</td>
                        <td style={{ textAlign: 'center', fontWeight: 700, color: '#2dce89' }}>{d.hadir}</td>
                        <td style={{ textAlign: 'center', fontWeight: 600, color: '#065f46' }}>{d.tepat}</td>
                        <td style={{ textAlign: 'center', fontWeight: 600, color: d.terlambat > 0 ? '#d97706' : 'inherit' }}>{d.terlambat}</td>
                        <td style={{ textAlign: 'center' }}>
                          <button
                            className="btn btn-secondary"
                            style={{ padding: '4px 10px', fontSize: '0.72rem', display: 'inline-flex', alignItems: 'center', gap: 4 }}
                            onClick={() => setDetailOrang({ ...d, _bulan: rekapOrangBulan })}
                          >
                            <Eye size={13} /> Detail
                          </button>
                        </td>
                      </tr>
                    ))}
                    {rekapOrangData.data.length === 0 && (
                      <tr><td colSpan={9} className="text-center text-muted" style={{ padding: 40 }}>
                        Tidak ada data untuk bulan dan filter tersebut.
                      </td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <div className="page-loading"><p className="text-muted">Pilih bulan untuk melihat rekap per karyawan.</p></div>
          )}
        </div>
      )}

      {/* Tab: Rekap Bulanan */}
      {activeTab === 'rekap' && (
        <div>
          {/* Month Picker & Actions */}
          <div className="filters" style={{ marginBottom: 20 }}>
            <div className="search-box">
              <Calendar size={18} />
              <input
                type="month"
                value={rekapBulan}
                onChange={e => setRekapBulan(e.target.value)}
                style={{ border: 'none', outline: 'none', fontSize: '0.9rem', fontWeight: 600, background: 'transparent' }}
              />
            </div>
            <div className="search-box">
              <Search size={18} />
              <input
                type="text"
                placeholder="Cari karyawan..."
                value={rekapSearch}
                onChange={e => setRekapSearch(e.target.value)}
              />
            </div>
            {rekapData && (
              <button className="btn btn-primary" onClick={generateRekapPDF}
                style={{ display: 'flex', alignItems: 'center', gap: 6, marginLeft: 'auto' }}>
                <FileText size={16} /> Export PDF
              </button>
            )}
          </div>

          {rekapLoading ? (
            <div className="page-loading"><div className="spinner-lg" /><p>Memuat rekap...</p></div>
          ) : rekapData ? (
            <>
              {/* Summary Cards */}
              <div className="dash-stats-row" style={{ marginBottom: 20 }}>
                <div className="dash-stat-col">
                  <div className="stat-card">
                    <div className="stat-card-body">
                      <div className="stat-card-content">
                        <h5 className="stat-card-label">Hari Kerja</h5>
                        <span className="stat-card-value" style={{ color: '#11cdef' }}>{totalHari}</span>
                        <span className="stat-card-sublabel">hari tercatat</span>
                      </div>
                      <div className="stat-card-icon-col">
                        <div className="stat-icon-shape" style={{ background: '#e6fafc', color: '#11cdef' }}>
                          <Calendar size={22} />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="dash-stat-col">
                  <div className="stat-card">
                    <div className="stat-card-body">
                      <div className="stat-card-content">
                        <h5 className="stat-card-label">Total Hadir</h5>
                        <span className="stat-card-value" style={{ color: '#2dce89' }}>{totalHadirRekap}</span>
                        <span className="stat-card-sublabel">{totalHari > 0 ? Math.round(totalHadirRekap / totalHari) : 0}/hari</span>
                      </div>
                      <div className="stat-card-icon-col">
                        <div className="stat-icon-shape" style={{ background: '#d1fae5', color: '#2dce89' }}>
                          <CheckCircle size={22} />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="dash-stat-col">
                  <div className="stat-card">
                    <div className="stat-card-body">
                      <div className="stat-card-content">
                        <h5 className="stat-card-label">Total Alpha</h5>
                        <span className="stat-card-value" style={{ color: '#f5365c' }}>{totalAlphaRekap}</span>
                        <span className="stat-card-sublabel">{totalHari > 0 ? Math.round(totalAlphaRekap / totalHari) : 0}/hari</span>
                      </div>
                      <div className="stat-card-icon-col">
                        <div className="stat-icon-shape" style={{ background: '#fee2e2', color: '#f5365c' }}>
                          <X size={22} />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="dash-stat-col">
                  <div className="stat-card">
                    <div className="stat-card-body">
                      <div className="stat-card-content">
                        <h5 className="stat-card-label">Kehadiran</h5>
                        <span className="stat-card-value" style={{ color: '#fb6340' }}>
                          {totalHadirRekap + totalAbsenRekap > 0
                            ? Math.round(totalHadirRekap / (totalHadirRekap + totalAbsenRekap) * 100)
                            : 0}%
                        </span>
                        <span className="stat-card-sublabel">rata-rata kehadiran</span>
                      </div>
                      <div className="stat-card-icon-col">
                        <div className="stat-icon-shape" style={{ background: '#fef3c7', color: '#fb6340' }}>
                          <TrendingUp size={22} />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Stacked Bar Chart — Daily Breakdown */}
              {rekapData.daily?.length > 0 && (
                <div className="table-wrapper" style={{ marginBottom: 20, padding: '20px 24px' }}>
                  <h4 style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--gray-500)', marginBottom: 16 }}>
                    Grafik Kehadiran Harian — {rekapBulan}
                  </h4>

                  {/* Legend */}
                  <div style={{ display: 'flex', gap: 16, marginBottom: 12, flexWrap: 'wrap' }}>
                    {STATUS_KEYS.map(s => (
                      <div key={s} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.75rem', color: 'var(--gray-400)' }}>
                        <span style={{ width: 10, height: 10, borderRadius: 2, background: STATUS_COLORS[s], display: 'inline-block' }} />
                        {s}
                      </div>
                    ))}
                  </div>

                  {/* Bars */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    {rekapData.daily.map(d => {
                      const total = d.total || 1;
                      return (
                        <div key={d.tanggal} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{
                            fontSize: '0.7rem', color: 'var(--gray-400)', width: 40, textAlign: 'right',
                            fontVariantNumeric: 'tabular-nums', flexShrink: 0
                          }}>
                            {d.tanggal.slice(8, 10)}
                          </span>
                          <div style={{
                            flex: 1, height: 18, borderRadius: 4, background: '#f0f0f0',
                            display: 'flex', overflow: 'hidden', transition: 'all 0.2s'
                          }}>
                            {STATUS_KEYS.map(s => {
                              const val = d[s.toLowerCase()] || 0;
                              if (val === 0) return null;
                              const widthPct = (val / total) * 100;
                              return (
                                <div
                                  key={s}
                                  title={`${s}: ${val}`}
                                  style={{
                                    width: `${widthPct}%`,
                                    background: STATUS_COLORS[s],
                                    transition: 'width 0.3s ease',
                                    minWidth: val > 0 ? 4 : 0,
                                  }}
                                />
                              );
                            })}
                          </div>
                          <span style={{
                            fontSize: '0.7rem', color: 'var(--gray-500)', width: 16, textAlign: 'center',
                            fontWeight: 600, fontVariantNumeric: 'tabular-nums'
                          }}>
                            {d.total}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* View toggle: Ringkasan vs Roster */}
              <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
                <button
                  onClick={() => setRekapView('ringkasan')}
                  className={`btn ${rekapView === 'ringkasan' ? 'btn-primary' : 'btn-secondary'}`}
                  style={{ padding: '6px 14px', fontSize: '0.8rem' }}
                >
                  <BarChart3 size={14} /> Ringkasan
                </button>
                <button
                  onClick={() => setRekapView('roster')}
                  className={`btn ${rekapView === 'roster' ? 'btn-primary' : 'btn-secondary'}`}
                  style={{ padding: '6px 14px', fontSize: '0.8rem' }}
                >
                  <Calendar size={14} /> Roster Harian
                </button>
              </div>

              {/* Roster Harian — grid karyawan x tanggal (kolom 1..31) */}
              {rekapView === 'roster' && (
                <RekapRoster rekapData={rekapData} bulan={rekapBulan} onOpenDetail={setDetailItem} />
              )}

              {/* Employee Recap Table */}
              {rekapView !== 'roster' && (
              <div className="table-wrapper">
                <h4 style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--gray-500)', marginBottom: 8 }}>
                  Rekap Per Karyawan — {rekapBulan}
                </h4>
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>NIK</th>
                      <th>Nama</th>
                      <th style={{ color: '#2dce89' }}>Hadir</th>
                      <th style={{ color: '#11cdef' }}>Izin</th>
                      <th style={{ color: '#fb6340' }}>Sakit</th>
                      <th style={{ color: '#11cdef' }}>Cuti</th>
                      <th style={{ color: '#f5365c' }}>Alpha</th>
                      <th>Total</th>
                      <th>% Hadir</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredKaryawan.map(k => {
                      const pctHadir = k.total > 0 ? Math.round(k.hadir / k.total * 100) : 0;
                      return (
                        <tr key={k.karyawan_id}>
                          <td><code>{k.nik}</code></td>
                          <td><strong>{k.nama}</strong></td>
                          <td style={{ fontWeight: 600, color: '#2dce89' }}>{k.hadir}</td>
                          <td>{k.izin}</td>
                          <td>{k.sakit}</td>
                          <td>{k.cuti}</td>
                          <td style={{ fontWeight: 600, color: k.alpha > 0 ? '#f5365c' : 'inherit' }}>{k.alpha}</td>
                          <td style={{ fontWeight: 600 }}>{k.total}</td>
                          <td>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              <div style={{
                                flex: 1, height: 6, borderRadius: 3, background: '#e9ecef',
                                maxWidth: 60, overflow: 'hidden'
                              }}>
                                <div style={{
                                  width: `${pctHadir}%`, height: '100%', borderRadius: 3,
                                  background: pctHadir >= 80 ? '#2dce89' : pctHadir >= 50 ? '#fb6340' : '#f5365c',
                                  transition: 'width 0.3s'
                                }} />
                              </div>
                              <span style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--gray-500)', width: 36 }}>
                                {pctHadir}%
                              </span>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                    {filteredKaryawan.length === 0 && (
                      <tr><td colSpan={9} className="text-center text-muted" style={{ padding: 40 }}>
                        Tidak ada data absensi untuk bulan ini.
                      </td></tr>
                    )}
                  </tbody>
                </table>
              </div>
              )}
            </>
          ) : (
            <div className="page-loading"><p className="text-muted">Pilih bulan untuk melihat rekap.</p></div>
          )}
        </div>
      )}

      {/* Detail Absensi Modal (detail per hari) */}
      <DetailAbsensiModal
        item={detailItem}
        onClose={() => setDetailItem(null)}
        onViewPhoto={(url, label) => setPhotoViewer({ url, label })}
      />

      {/* Detail Rekap 1 Karyawan */}
      <DetailOrangModal
        item={detailOrang}
        onClose={() => setDetailOrang(null)}
      />

      {/* Photo Viewer Lightbox */}
      {photoViewer && (
        <div className="modal-overlay" style={{ zIndex: 2000, background: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => setPhotoViewer(null)}>
          <div style={{ position: 'relative', maxWidth: '95vw', maxHeight: '95vh', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <button className="btn-icon" style={{
              position: 'absolute', top: -40, right: -40, color: '#fff', zIndex: 2001,
              background: 'rgba(255,255,255,0.15)', borderRadius: '50%', width: 40, height: 40,
              border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'all 0.2s',
            }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.3)'}
              onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.15)'}
              onClick={() => setPhotoViewer(null)}>
              <X size={22} />
            </button>
            <p style={{ color: '#fff', marginBottom: 12, fontSize: '0.9rem', fontWeight: 600, textShadow: '0 2px 8px rgba(0,0,0,0.5)' }}>
              {photoViewer.label}
            </p>
            <img src={photoViewer.url} alt="Selfie"
              style={{
                maxWidth: '100%', maxHeight: '75vh', objectFit: 'contain',
                borderRadius: 12, boxShadow: '0 8px 40px rgba(0,0,0,0.4)',
                border: '2px solid rgba(255,255,255,0.1)',
              }}
              onClick={e => e.stopPropagation()}
            />
          </div>
        </div>
      )}

      {/* Camera Capture Modal */}
      <CameraCapture
        show={showCamera}
        onCapture={handleCameraCapture}
        onClose={() => { setShowCamera(false); setCheckinTarget(null); }}
        facingMode="user"
      />

      {/* GPS Capture + Konfirmasi Check-in/out */}
      {capturedPhoto && checkinTarget && (
        <div className="modal-overlay" onClick={cancelCheckin}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 440 }}>
            <div className="modal-header">
              <h3 style={{ fontSize: '1rem' }}>
                <Camera size={16} style={{ marginRight: 8, verticalAlign: 'middle', color: '#2dce89' }} />
                {cameraMode === 'checkin' ? 'Konfirmasi Check-in' : 'Konfirmasi Check-out'}
                <span style={{ fontSize: '0.8rem', color: 'var(--gray-400)', fontWeight: 400, marginLeft: 8 }}>
                  {checkinTarget.nama}
                </span>
              </h3>
              <button className="btn-icon" onClick={cancelCheckin}><X size={18} /></button>
            </div>
            <div className="modal-body">
              {/* Photo Preview */}
              <div style={{
                background: '#f3f4f6', borderRadius: 'var(--radius)', marginBottom: 12,
                overflow: 'hidden', maxHeight: 240, display: 'flex', justifyContent: 'center'
              }}>
                <img src={capturedPhoto} alt="Selfie"
                  style={{ maxWidth: '100%', maxHeight: 240, objectFit: 'contain' }}
                />
              </div>

              {/* GPS Location */}
              <LocationCapture onLocation={handleGpsReady} autoCapture={true} />

              {/* Info */}
              <div style={{
                padding: '10px 14px', background: gpsReady ? '#d1fae5' : '#fef3c7',
                borderRadius: 'var(--radius)', fontSize: '0.78rem',
                color: gpsReady ? '#065f46' : '#92400e'
              }}>
                {gpsReady
                  ? '✅ Lokasi GPS siap. Klik konfirmasi untuk ' + (cameraMode === 'checkin' ? 'check-in.' : 'check-out.')
                  : '⏳ Mendapatkan lokasi GPS... Silakan tunggu.'}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={cancelCheckin}>Batal</button>
              <button
                className={`btn ${cameraMode === 'checkin' ? 'btn-primary' : 'btn-danger'}`}
                onClick={cameraMode === 'checkin' ? submitCheckin : submitCheckout}
                disabled={checkinLoading || !gpsReady}
              >
                {checkinLoading ? (
                  <><div className="spinner" style={{ width: 14, height: 14, borderWidth: 2 }} /> Memproses...</>
                ) : (
                  <><Navigation size={14} /> {cameraMode === 'checkin' ? 'Konfirmasi Check-in' : 'Konfirmasi Check-out'}</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content modal-lg" onClick={e => e.stopPropagation()} style={{ maxWidth: 560 }}>
            <div className="modal-header">
              <h3>{editing ? 'Edit Absensi' : 'Tambah Absensi'}</h3>
              <button className="btn-icon" onClick={() => setShowModal(false)}><X size={18} /></button>
            </div>
            <div className="modal-body">
              {error && <div className="form-error"><AlertCircle size={14} /><span>{error}</span></div>}
              <div className="form-grid">
                <div className="form-group">
                  <label>Karyawan *</label>
                  <select value={form.karyawan_id} onChange={e => setForm({...form, karyawan_id: e.target.value})}>
                    <option value="">-- Pilih Karyawan --</option>
                    {karyawan.map(k => (
                      <option key={k.id} value={k.id}>{k.nik} - {k.nama}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Tanggal *</label>
                  <input type="date" value={form.tanggal} onChange={e => setForm({...form, tanggal: e.target.value})} />
                </div>
                <div className="form-group">
                  <label>Check-in</label>
                  <input type="time" value={form.check_in} onChange={e => setForm({...form, check_in: e.target.value})} />
                </div>
                <div className="form-group">
                  <label>Check-out</label>
                  <input type="time" value={form.check_out} onChange={e => setForm({...form, check_out: e.target.value})} />
                </div>
                <div className="form-group">
                  <label>Status</label>
                  <select value={form.status} onChange={e => setForm({...form, status: e.target.value})}>
                    {STATUS_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Keterangan</label>
                  <input type="text" value={form.keterangan} onChange={e => setForm({...form, keterangan: e.target.value})} placeholder="Keterangan..." />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowModal(false)}>Batal</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Menyimpan...' : 'Simpan'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
